import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogoMark, Logout } from "../icons";
import { Button } from "./index";
import { FadeIn } from "./motion";

/** Product name shown in the top bar. Single place to rebrand. */
export const BRAND_NAME = "CampusCare Learn";

/**
 * The three places a learner can be. Kept as data so the tab strip, the
 * breadcrumb and the active-state logic can never drift apart.
 *
 * "Inside a section" is only reachable once a section has been chosen — the
 * section workspace reads `localStorage.section`, so offering the tab without
 * one would land the learner on an empty screen. Clicking it with no section
 * selected sends them back to the dashboard to pick one.
 */
const TABS = [
  { key: "dashboard", label: "Home dashboard", to: "/dashboard" },
  { key: "plan", label: "My Learning Plan", to: "/my-learning" },
  { key: "section", label: "Inside a section", to: "/modules" },
];

const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "—";

/**
 * The persistent chrome around every learner screen: brand + breadcrumb, the
 * user block with logout, and the primary tab strip.
 *
 * Screens supply only their own content — they never re-render navigation, so
 * moving between them feels like one application rather than three pages.
 */
export const AppShell = ({
  crumb,
  active,
  wide = false,
  flush = false,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const userName = localStorage.getItem("userName") || "Learner";
  const role = localStorage.getItem("role") || "Learner";

  // Derive the active tab from the route when the screen doesn't state one.
  const activeKey =
    active ||
    TABS.find((t) => t.to === location.pathname)?.key ||
    undefined;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("savedLayouts");
    navigate("/");
  };

  const goToTab = (tab) => {
    if (tab.key === "section" && !localStorage.getItem("section")) {
      navigate("/dashboard");
      return;
    }
    navigate(tab.to);
  };

  return (
    <div className={`ds-shell${wide ? " ds-shell--wide" : ""}`}>
      <header className="ds-topbar">
        <div className="ds-topbar__inner">
          <button
            className="ds-brand"
            onClick={() => navigate("/dashboard")}
            title="Go to dashboard"
          >
            <span className="ds-brand__mark">
              <LogoMark size={19} />
            </span>
            <span className="ds-brand__text">
              <span className="ds-brand__name">{BRAND_NAME}</span>
              <span className="ds-brand__crumb">
                Dashboard{crumb ? <> / <strong>{crumb}</strong></> : null}
              </span>
            </span>
          </button>

          <span className="ds-topbar__spacer" />

          <div className="ds-user">
            <div className="ds-user__meta">
              <div className="ds-user__name">{userName}</div>
              <div className="ds-user__role">{role}</div>
            </div>
            <span className="ds-avatar">{initials(userName)}</span>
          </div>

          <Button variant="secondary" size="sm" icon={Logout} onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </header>

      <nav className="ds-tabnav">
        <div className="ds-tabnav__group">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className="ds-tabnav__item"
              aria-current={activeKey === tab.key ? "page" : undefined}
              onClick={() => goToTab(tab)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      <main
        className={[
          "ds-page",
          wide && "ds-page--wide",
          flush && "ds-page--flush",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* Keyed on the route so moving between tabs re-runs the entrance —
            the page reads as arriving rather than as a hard cut. */}
        <FadeIn key={location.pathname}>{children}</FadeIn>
      </main>
    </div>
  );
};

export default AppShell;
